import React, { useReducer, useRef, useCallback } from 'react';
import PropTypes from 'prop-types';

import Spinner from '../../shared/spinner.component';
import DropdownMenu from '../../shared/dropdownMenu';
import FileUploadButton from '../../shared/fileUpload/fileUploadButton.component';
import { acceptedExcelFileExtensions } from '../../shared/fileUpload/constants';

import { waitForSpinnerToShowUp } from '../../shared/cursorHelpers';

import { generateAssetPath } from '../../utils/assetsHelpers';

const HamburgerDropdown = ({
  canModify,
  showModalForAdd,
  addEntityMessage,
  disableAddingEntity,
  fileToImportChange,
  fileToImportSelect,
  disableImporting,
  templateFileName,
  templatePath,
  generateTemplate,
  disableGeneratingTemplate,
  exportToExcel,
  disableExporting,
  additionalItems,
  exportLabel = 'Export to Excel',
  hideImporting,
  hideAdd,
}) => {
  const dropdownRef = useRef(null);

  const [state, setState] = useReducer(
    (state, newState) => ({ ...state, ...newState }),
    { isGeneratingTemplate: false, isGeneratingExport: false }
  );

  const hideDropdown = useCallback(() => {
    dropdownRef.current.hideDropDown();
  }, []);

  const onFileUploadButtonClick = useCallback(
    event => {
      fileToImportSelect(hideDropdown)();
      event.target.value = null;
    },
    [fileToImportSelect, hideDropdown]
  );

  const downloadTemplate = useCallback(
    () => {
      setState({ isGeneratingTemplate: true });
      waitForSpinnerToShowUp({
        action: generateTemplate,
        callback: () => {
          hideDropdown();
          setState({ isGeneratingTemplate: false });
        },
      });
    },
    [generateTemplate, hideDropdown]
  );

  const exportToExcelHandler = useCallback(
    () => {
      setState({ isGeneratingExport: true });
      waitForSpinnerToShowUp({
        action: exportToExcel,
        callback: () => setState({ isGeneratingExport: false }),
      });
    },
    [exportToExcel]
  );

  const showModalForAddHandler = useCallback(
    () => showModalForAdd ? showModalForAdd() : null,
    [showModalForAdd]
  );

  const addEntityComponent = showModalForAdd && addEntityMessage && canModify && !hideAdd
    ? (
      <button
        className="hamburger-action link-button"
        tabIndex="0"
        onClick={showModalForAddHandler}
        disabled={disableAddingEntity}
      >
        {addEntityMessage}
      </button>
    )
    : null;

  const chooseFileToImportComponent = fileToImportChange && fileToImportSelect && canModify && !hideImporting
    ? (
      <FileUploadButton
        accept={acceptedExcelFileExtensions}
        className="upload-file-button hamburger-action"
        onChange={fileToImportChange}
        onClick={onFileUploadButtonClick}
        disabled={disableImporting}
      >
        Choose File to Import
      </FileUploadButton>
    )
    : null;

  const renderDownloadImportTemplate = () => {
    if (!canModify || (!generateTemplate && !templatePath) || hideImporting) {
      return null;
    }
    if (generateTemplate) {
      return (
        <button
          className="hamburger-action link-button"
          tabIndex="2"
          onClick={downloadTemplate}
          disabled={disableGeneratingTemplate}
        >
          {state.isGeneratingTemplate
            ? (
              <span>
                <Spinner small /> Exporting
              </span>
            )
            : 'Download Import Template'
          }
        </button>
      );
    }
    return (
      <a
        className="hamburger-action"
        role="button"
        tabIndex="1"
        download={templateFileName}
        href={generateAssetPath(templatePath)}
        onClick={hideDropdown}
      >
        Download Import Template
      </a>
    );
  };

  const exportToExcelComponent = exportToExcel
    ? (
      <button
        className="hamburger-action link-button"
        tabIndex="0"
        onClick={exportToExcelHandler}
        disabled={disableExporting}
      >
        {state.isGeneratingExport
          ? (
            <span>
              <Spinner small /> Exporting
            </span>
          ) : exportLabel
        }
      </button>
    )
    : null;

  return (
    <DropdownMenu ref={dropdownRef} contentClassName="hamburger-menu">
      {addEntityComponent}
      {chooseFileToImportComponent}
      {renderDownloadImportTemplate()}
      {exportToExcelComponent}
      {additionalItems}
    </DropdownMenu>
  );
};

HamburgerDropdown.propTypes = {
  canModify: PropTypes.bool,
  showModalForAdd: PropTypes.func,
  addEntityMessage: PropTypes.string,
  disableAddingEntity: PropTypes.bool,
  fileToImportChange: PropTypes.func,
  fileToImportSelect: PropTypes.func,
  disableImporting: PropTypes.bool,
  disableExporting: PropTypes.bool,
  exportLabel: PropTypes.string,
  templateFileName: PropTypes.string,
  templatePath: PropTypes.string,
  generateTemplate: PropTypes.func,
  disableGeneratingTemplate: PropTypes.bool,
  exportToExcel: PropTypes.func,
  additionalItems: PropTypes.arrayOf(PropTypes.node),
  hideImporting: PropTypes.bool,
  hideAdd: PropTypes.bool,
};

export default HamburgerDropdown;
